import { useState, useEffect } from 'react';
import { api } from './api';

// =============================================================================
//  UpworkSetup
//  -------------------------------------------------------------------------
//  The "Setup" tab: walks each user through connecting THEIR OWN Upwork
//  account (OAuth). Under our model there is one shared Up-Tracker application
//  registered with Upwork; every user authorizes their own account and only
//  ever sees their own data. Users do NOT apply for their own Upwork API key.
//
//  Connect is a full-page redirect (server 302s to Upwork's consent screen),
//  so it's window.location, not a fetch. The callback returns to /?upwork_connected=1.
// =============================================================================
export default function UpworkSetup() {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState(false);
  const justConnected = new URLSearchParams(window.location.search).get('upwork_connected') === '1';

  async function load() {
    setLoading(true); setError('');
    try {
      setStatus(await api.getUpworkStatus());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function handleConnect() {
    // Full-page navigation: the backend builds the PKCE authorize URL and 302s
    // the browser to Upwork. After consent, Upwork returns to our callback.
    window.location.href = api.connectUpworkUrl;
  }

  async function handleDisconnect() {
    if (!window.confirm('Disconnect your Upwork account? Up-Tracker will stop syncing until you reconnect. Your existing data is kept.')) return;
    setBusy(true); setError('');
    try {
      await api.disconnectUpwork();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const connected = status?.connected === true;

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>Connect Upwork</h2>
      <p style={{ color: '#737370', fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
        Link your own Upwork account so Up-Tracker can pull your time logs and
        transactions automatically. You authorize Up-Tracker through Upwork's
        secure sign-in — we never see your Upwork password.
      </p>

      {error && <p className="error-msg">{error}</p>}

      {justConnected && !error && (
        <div style={{
          background: '#DCFCE7', border: '1px solid #16a34a', color: '#166534',
          borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16,
        }}>
          ✓ Upwork connected. Your data will sync shortly.
        </div>
      )}

      {/* ── Connection status card ── */}
      <div style={{
        border: '1px solid #e5e5e3', borderRadius: 10, padding: '18px 20px',
        marginBottom: 24, background: '#fff',
      }}>
        {loading ? (
          <span style={{ color: '#737370', fontSize: 13 }}>Checking connection…</span>
        ) : connected ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                Connected
              </div>
              <div style={{ color: '#737370', fontSize: 12, marginTop: 6, lineHeight: 1.7 }}>
                {status.connected_email && <>Account: <b>{status.connected_email}</b><br /></>}
                {status.last_sync_utc
                  ? <>Last sync: {new Date(status.last_sync_utc).toLocaleString()}</>
                  : <>Not synced yet — your first sync runs automatically.</>}
              </div>
            </div>
            <button className="btn-outline btn-danger" onClick={handleDisconnect} disabled={busy}>
              {busy ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#a8a8a3', display: 'inline-block' }} />
                Not connected
              </div>
              <div style={{ color: '#737370', fontSize: 12, marginTop: 6 }}>
                Connect to start syncing your Upwork time logs and earnings.
              </div>
            </div>
            <button className="btn-primary" onClick={handleConnect}>Connect Upwork account</button>
          </div>
        )}
      </div>

      {/* ── What happens / privacy ── */}
      <Section title="What Up-Tracker accesses">
        <ul style={ulStyle}>
          <li>Your Upwork <b>freelancer profile</b> (to identify your account)</li>
          <li>Your <b>time logs</b> (hours you tracked on Upwork)</li>
          <li>Your <b>transactions</b> (earnings, fees, withdrawals)</li>
        </ul>
        <p style={pStyle}>
          Access is <b>read-only</b> — Up-Tracker never changes anything in your
          Upwork account. You can disconnect at any time, which immediately stops
          syncing.
        </p>
      </Section>

      <Section title="Your data is private to you">
        <p style={pStyle}>
          Up-Tracker is multi-tenant: each user's Upwork data is <b>strictly
          isolated</b>. No other user — and no administrator — can see your time
          logs, earnings, or client information. Isolation is enforced at the
          database layer (row-level security), not just in the app.
        </p>
      </Section>

      <Section title="How connecting works">
        <ol style={olStyle}>
          <li>Click <b>Connect Upwork account</b> above.</li>
          <li>You're taken to Upwork's secure sign-in and asked to authorize Up-Tracker.</li>
          <li>Approve, and Upwork sends you back here — connected. That's it.</li>
        </ol>
        <p style={{ ...pStyle, color: '#a8a8a3' }}>
          Note: Upwork's authorization can take a few seconds. If you land back
          here still showing "Not connected", refresh the page.
        </p>
      </Section>

      <details style={{ marginTop: 8, fontSize: 12, color: '#a8a8a3' }}>
        <summary style={{ cursor: 'pointer' }}>For administrators</summary>
        <p style={{ ...pStyle, color: '#737370' }}>
          End users do <b>not</b> register their own Upwork API key — there is a
          single Up-Tracker application registered with Upwork, and each user
          authorizes it against their own account. The exact field-by-field
          answers for getting that one Upwork developer key approved live in the
          repo at <code>docs/UPWORK_API_APPLICATION.md</code>.
        </p>
      </details>
    </div>
  );
}

const ulStyle = { margin: '0 0 10px', paddingLeft: 18, color: '#1a1a1a', fontSize: 13, lineHeight: 1.9 };
const olStyle = { margin: '0 0 10px', paddingLeft: 18, color: '#1a1a1a', fontSize: 13, lineHeight: 1.9 };
const pStyle  = { color: '#1a1a1a', fontSize: 13, lineHeight: 1.7, margin: '0 0 6px' };

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
        color: '#737370', borderBottom: '1px solid #e5e5e3', paddingBottom: 5, marginBottom: 10,
      }}>{title}</div>
      {children}
    </div>
  );
}
